/**
 * Hook for fetching and managing exhibit data
 *
 * This hook provides an interface for components to access exhibit data
 * and abstracts away the implementation details of data fetching.
 */

import { useCallback, useEffect, useState } from 'react';
import { exhibitService } from '../services';
import { Exhibit, SearchFilters } from '../types'; // Reverted to relative import

interface UseExhibitsResult {
    exhibits: Exhibit[];
    exhibit: Exhibit | null;
    isLoading: boolean;
    error: Error | null;
    totalCount: number;
    currentPage: number;
    lastPage: number;
    fetchExhibits: (filters?: SearchFilters) => Promise<void>;
    fetchExhibitDetail: (id: number) => Promise<Exhibit | null>;
    searchExhibits: (query: string, filters?: Omit<SearchFilters, 'query'>) => Promise<void>;
}

/**
 * Hook for fetching and managing exhibit data
 */
export function useExhibits(initialFilters?: SearchFilters): UseExhibitsResult {
    // State for exhibit data and loading/error states
    const [exhibits, setExhibits] = useState<Exhibit[]>([]);
    const [exhibit, setExhibit] = useState<Exhibit | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    // Pagination state
    const [totalCount, setTotalCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [lastPage, setLastPage] = useState<number>(1);

    /**
     * Fetch exhibits based on optional filters
     */
    const fetchExhibits = useCallback(async (filters?: SearchFilters) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await exhibitService.getExhibits(filters);

            setExhibits(response.data);
            setTotalCount(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setLastPage(response.meta.last_page);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch exhibits'));
            console.error('Error fetching exhibits:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Fetch a single exhibit by ID
     */
    const fetchExhibitDetail = useCallback(async (id: number): Promise<Exhibit | null> => {
        setIsLoading(true);
        setError(null);

        try {
            const exhibit = await exhibitService.getExhibit(id);
            setExhibit(exhibit);
            return exhibit;
        } catch (err) {
            setError(err instanceof Error ? err : new Error(`Failed to fetch exhibit with ID: ${id}`));
            console.error(`Error fetching exhibit with ID ${id}:`, err);
            setExhibit(null);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Search exhibits by query string
     */
    const searchExhibits = useCallback(async (query: string, filters?: Omit<SearchFilters, 'query'>) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await exhibitService.searchExhibits(query, filters);

            setExhibits(response.data);
            setTotalCount(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setLastPage(response.meta.last_page);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to search exhibits'));
            console.error('Error searching exhibits:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch exhibits on initial render if initialFilters are provided
    useEffect(() => {
        if (initialFilters) {
            fetchExhibits(initialFilters);
        }
    }, [fetchExhibits, initialFilters]);

    return {
        exhibits,
        exhibit,
        isLoading,
        error,
        totalCount: totalCount,
        lastPage,
        currentPage,
        fetchExhibits,
        fetchExhibitDetail,
        searchExhibits,
    };
}
